import { useRef, useState, type ReactNode } from 'react';

import { cn } from '../lib/classnames';
import './PullToRefresh.css';

const THRESHOLD = 60; // px — drag distance to commit a refresh

/** Touch pull-to-refresh wrapper around a scrollable area.
 *
 *  Ported from the v5 prototype (lines 6008-6057). Behaviour: while
 *  scrollTop is 0, a downward touch drag adds an indicator strip whose
 *  height is 45 % of the drag distance (capped at 1.5× THRESHOLD).
 *  Releasing past the threshold triggers ``onRefresh`` and shows a
 *  spinning glyph for ~700 ms.
 */
export function PullToRefresh({
  children,
  onRefresh,
}: {
  children: ReactNode;
  onRefresh: () => void | Promise<unknown>;
}) {
  const [pulled, setPulled] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop > 0) return;
    startY.current = e.touches[0]?.clientY ?? 0;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!startY.current) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
    if (dy > 0 && containerRef.current && containerRef.current.scrollTop === 0) {
      setPulled(Math.min(dy * 0.45, THRESHOLD * 1.5));
    }
  };

  const onTouchEnd = async () => {
    startY.current = 0;
    if (pulled >= THRESHOLD) {
      setRefreshing(true);
      setPulled(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        // Brief settle so the spinner is visible even on a fast refetch.
        await new Promise((r) => setTimeout(r, 350));
        setRefreshing(false);
      }
    }
    setPulled(0);
  };

  const visible = pulled > 5 || refreshing;
  const indicatorText = refreshing ? '⟳' : pulled >= THRESHOLD ? '↑ отпусти' : '↓ тяни';

  return (
    <div
      ref={containerRef}
      className="screen ptr-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="ptr-indicator"
        style={{
          height: visible ? Math.max(pulled, refreshing ? 36 : 0) : 0,
          opacity: visible ? 1 : 0,
        }}
      >
        <div
          className={cn(
            'ptr-spinner',
            refreshing && 'spinning',
            pulled >= THRESHOLD && 'ready',
          )}
        >
          {indicatorText}
        </div>
      </div>
      {children}
    </div>
  );
}
